import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Headset, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../api/client';

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // Куда вернуться после успешного входа (если попали сюда по защищённому URL).
  const returnTo: string =
    (loc.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)
      ?.from?.pathname
      ? (loc.state as any).from.pathname
        + ((loc.state as any).from.search || '')
        + ((loc.state as any).from.hash || '')
      : '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sso, setSso] = useState<{ enabled: boolean; button_text?: string }>({ enabled: false });
  const [sp] = useSearchParams();

  useEffect(() => {
    api.get<{ enabled: boolean; button_text?: string }>('/api/v1/auth/keycloak/config')
      .then(r => setSso(r.data)).catch(() => setSso({ enabled: false }));
    const ssoError = sp.get('sso_error');
    if (ssoError) setErr(`SSO: ${decodeURIComponent(ssoError)}`);
  }, [sp]);

  const goSso = () => {
    const ret = encodeURIComponent(returnTo);
    window.location.href = `/api/v1/auth/keycloak/start?return_to=${ret}`;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      if (needTotp) {
        // Второй шаг: login с TOTP-кодом
        const r = await fetch('/api/v1/auth/login/totp', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: email, password, code }),
        });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || 'неверный TOTP-код');
        }
        const data = await r.json();
        localStorage.setItem('sd.auth', JSON.stringify({
          token: data.access_token, refresh_token: data.refresh_token,
        }));
        window.location.replace(returnTo);
        return;
      }
      await login(email, password);
      nav(returnTo, { replace: true });
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '';
      if (detail === 'totp_required') {
        setNeedTotp(true);
        setErr(null);
      } else {
        setErr(detail || 'Ошибка входа');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-xl p-8 shadow-xl">
        <div className="flex flex-col items-center mb-6">
          <Headset className="text-blue-500 w-10 h-10 mb-2" />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent uppercase tracking-tight">
            Service Desk
          </h1>
          <p className="text-slate-500 text-xs mt-1">sd.devkz.ru</p>
        </div>

        <label className="block mb-3">
          <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Email</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
        </label>
        <label className="block mb-4">
          <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Пароль</span>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
        </label>

        {needTotp && (
          <label className="block mb-4">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Код из аутентификатора (6 цифр / backup-код)</span>
            <input type="text" inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={e => setCode(e.target.value)} required autoFocus
              placeholder="123456 или XXXX-XXXX"
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white font-mono focus:border-blue-500 outline-none" />
          </label>
        )}

        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-3">{err}</div>}

        <button type="submit" disabled={submitting || !email || !password}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-2 rounded transition flex items-center justify-center gap-2">
          {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
          Войти
        </button>

        {sso.enabled && (
          <>
            <div className="my-3 flex items-center gap-2 text-[10px] text-slate-600 uppercase">
              <div className="flex-1 h-px bg-slate-800" />
              или
              <div className="flex-1 h-px bg-slate-800" />
            </div>
            <button type="button" onClick={goSso}
              className="w-full bg-purple-700 hover:bg-purple-600 text-white font-bold py-2 rounded transition flex items-center justify-center gap-2">
              <KeyRound size={16} /> {sso.button_text || 'Войти через SSO'}
            </button>
          </>
        )}

        <a href="/forgot" className="block text-center text-xs text-blue-400 hover:underline mt-3">
          Забыли пароль?
        </a>

      </form>
    </div>
  );
}
